import React from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Component from '../../components/AscComponent';
import moment from 'moment';
import ReactLoading from 'react-loading';
import Encoding from 'encoding-japanese';
import '../Elements/Modal/Modal.css';

/**
 * 電話帳 CSVエクスポートモーダル
 */
export default class PhoneBookExportModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // エクスポート設定
      export_include_name: true,
      export_include_phone_number: true,
      export_include_memo: true,
      export_include_last_call: false,
      export_include_total_call_count: false,
      export_encoding: 'utf8',
      export_range: 'all',
      // ローディング状態
      export_processing: false,
    };
  }

  /**
   * チェックボックス変更ハンドラ
   */
  handleCheckboxChange = (fieldName) => (e) => {
    this.setState({ [fieldName]: e.target.checked });
  }

  /**
   * ラジオボタン変更ハンドラ
   */
  handleRadioChange = (fieldName, value) => () => {
    this.setState({ [fieldName]: value });
  }

  /**
   * 全データを取得
   *
   * 【#415】検索条件を引数で受け取る。
   * 出力範囲「すべてのデータ」は条件なし、「検索・フィルター条件を適用」は
   * 一覧が使っている検索条件をそのまま渡して、条件に一致する全件を取得する。
   *
   * @param {Array} filtered 一覧と同じ形式の検索条件。省略時は条件なし
   */
  fetchAllData = async (filtered = []) => {
    // エラーは呼び出し元（handleExport）で扱う。ここで try/catch して
    // そのまま再スローするだけの分岐は置かない。
    const response = await this.ascAxios('post', 'PhoneBook/board', {
      page: 0,
      pageSize: 999999, // 非常に大きな値を指定して全データを取得
      filtered,
      sorted: [{ id: 'id', desc: true }],
      include_call_history: false // CSV出力時は通話履歴不要（パフォーマンス最適化）
    });

    if (response && response.data && response.data.rows) {
      return response.data.rows;
    }
    return [];
  }

  /**
   * エクスポート実行
   */
  handleExport = async () => {
    try {
      this.setState({ export_processing: true });

      const {
        export_include_name,
        export_include_phone_number,
        export_include_memo,
        export_include_last_call,
        export_include_total_call_count,
        export_encoding,
        export_range,
      } = this.state;

      // 【#415】出力対象は全範囲ともサーバーから取り直すため、
      // 一覧が表示しているページのデータ（props.state.data）は参照しない。
      const { selectedData } = this.props.state;

      // エクスポート対象データを決定
      let exportData = [];
      if (export_range === 'all') {
        // 全データを取得
        exportData = await this.fetchAllData();
      } else if (export_range === 'filtered') {
        // 【#415】検索条件に一致する全件を取得する。
        // 従来は props.state.data（現在のページ）をそのまま使っていたため、
        // 50件ヒットしていてもページサイズ分（20件）しか出力されなかった。
        exportData = await this.fetchAllData(this.props.state.filtered || []);
      } else if (export_range === 'selected') {
        // 【#415】この分岐に対応するラジオは描画されておらず、画面からは選べない。
        // ラジオの追加は Issue #415 の範囲外のため、分岐だけを残している。
        if (!selectedData || selectedData.length === 0) {
          this.setState({ export_processing: false });
          return this.showErrorObjectMessage(this.props.langText.Message.CsvNoDataSelected);
        }
        exportData = selectedData;
      }

      if (exportData.length === 0) {
        this.setState({ export_processing: false });
        return this.showErrorObjectMessage(this.props.langText.Message.CsvNoDataSelected);
      }

      // CSV生成
      const csvData = this.generateCSV(exportData, {
        export_include_name,
        export_include_phone_number,
        export_include_memo,
        export_include_last_call,
        export_include_total_call_count,
      });

      // ダウンロード
      this.downloadCSV(csvData, export_encoding);

      this.setState({ export_processing: false });

      // モーダルを閉じる
      this.props.propSetState({ show: false });
      alert(this.props.langText.Body.PhoneBookExportSuccess);
    } catch (error) {
      this.setState({ export_processing: false });
      this.showErrorObjectMessage(this.props.langText.Body.PhoneBookExportError);
    }
  }

  /**
   * CSV生成
   */
  generateCSV = (data, options) => {
    const {
      export_include_name,
      export_include_phone_number,
      export_include_memo,
      export_include_last_call,
      export_include_total_call_count,
    } = options;

    // 電話番号の最大数を計算
    let maxPhoneNumbers = 1;
    data.forEach((item) => {
      const phoneCount = item.awt010_dest_tel_number ? item.awt010_dest_tel_number.length : 0;
      if (phoneCount > maxPhoneNumbers) {
        maxPhoneNumbers = phoneCount;
      }
    });

    // ヘッダー行を生成
    const headers = [];
    if (export_include_name) headers.push('名前');
    if (export_include_memo) headers.push('メモ');
    if (export_include_phone_number) {
      for (let i = 1; i <= maxPhoneNumbers; i++) {
        headers.push(`電話番号${i}`);
      }
    }
    if (export_include_last_call) headers.push('最終通話日時');
    if (export_include_total_call_count) headers.push('総通話件数');

    const rows = [headers];

    // データ行を生成
    data.forEach((item) => {
      const row = [];
      if (export_include_name) row.push(item.name || '');
      if (export_include_memo) row.push(item.memo || '');
      if (export_include_phone_number) {
        const phoneNumbers = item.awt010_dest_tel_number || [];
        for (let i = 0; i < maxPhoneNumbers; i++) {
          row.push(phoneNumbers[i] ? phoneNumbers[i].dest_tel_number || '' : '');
        }
      }
      if (export_include_last_call) {
        const lastCall = item.latest_call_history;
        if (lastCall && lastCall.answer_datetime) {
          // 日付を YYYY/MM/DD HH:mm:ss 形式に変換
          const formattedDate = moment(lastCall.answer_datetime).format('YYYY/MM/DD HH:mm:ss');
          row.push(formattedDate);
        } else {
          row.push('');
        }
      }
      if (export_include_total_call_count) {
        row.push(item.call_histories_total_count || '');
      }
      rows.push(row);
    });

    // CSV文字列に変換
    return rows.map(row => row.map(cell => {
      const str = String(cell);
      // カンマ、改行、ダブルクォートを含む場合はエスケープ
      if (str.includes(',') || str.includes('\n') || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(',')).join('\n');
  }

  /**
   * ローディング表示
   */
  renderLoadingOverlay = () => {
    const { export_processing } = this.state;
    const { langText } = this.props;

    if (export_processing) {
      return (
        <div className="asc-block-ui">
          <div className="asc-block-icon-box">
            <ReactLoading className="asc-block-icon" type="spinningBubbles" color="#fff" />
            <label className="asc-block-label">{langText.Table.LoadingText}</label>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * CSVダウンロード
   */
  downloadCSV = (csvData, encoding) => {
    let blob;
    if (encoding === 'sjis') {
      // Shift-JIS エンコード（encoding-japaneseライブラリを使用）
      // Unicode文字列をUnicode配列に変換
      const unicodeArray = [];
      for (let i = 0; i < csvData.length; i++) {
        unicodeArray.push(csvData.charCodeAt(i));
      }

      // Unicode配列をShift-JISバイト配列に変換
      const sjisArray = Encoding.convert(unicodeArray, {
        to: 'SJIS',
        from: 'UNICODE'
      });

      // Uint8Arrayに変換
      const uint8Array = new Uint8Array(sjisArray);
      blob = new Blob([uint8Array], { type: 'text/csv;charset=shift_jis' });
    } else {
      // UTF-8 (BOM付き)
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      blob = new Blob([bom, csvData], { type: 'text/csv;charset=utf-8' });
    }

    // ダウンロード
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // 日本時間（JST）のタイムスタンプを生成
    const now = new Date();
    const jstOffset = 9 * 60; // JST is UTC+9
    const jstTime = new Date(now.getTime() + (jstOffset + now.getTimezoneOffset()) * 60000);
    const timestamp = moment(jstTime).format('YYYYMMDD_HHmmss');

    a.download = `${this.props.langText.Body.ExportFileName}_${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  render() {
    const { langText } = this.props;
    const {
      export_include_name,
      export_include_phone_number,
      export_include_memo,
      export_include_last_call,
      export_include_total_call_count,
      export_encoding,
      export_range,
      export_processing,
    } = this.state;

    return (
      <Modal
        show={this.props.state.show}
        onHide={() => this.props.propSetState({ show: false })}
        size="lg"
        centered
        scrollable={true}
      >
        <div style={{ position: 'relative' }}>
          {this.renderLoadingOverlay()}
          <Modal.Header>
            <Modal.Title>
              <Row>
                <Col className="modal-title-text" lg={11} sm={11} xs={11}>
                  {langText.Body.PhoneBookExportTitle}
                </Col>
                <Col lg={1} sm={1} xs={1}>
                  <Button
                    className="x-execute"
                    onClick={() => this.props.propSetState({ show: false })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </Col>
              </Row>
            </Modal.Title>
          </Modal.Header>
        <Modal.Body>
          <Card variant="info">
            <Card.Header>
              <Card.Title>{langText.Body.ExportContentSelect}</Card.Title>
            </Card.Header>
            <Card.Body className="card-body-centered">
              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ExportBasicInfo}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="checkbox"
                    label={langText.Body.ExportBasicInfoName}
                    checked={export_include_name}
                    onChange={this.handleCheckboxChange('export_include_name')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label={langText.Body.ExportBasicInfoPhoneNumber}
                    checked={export_include_phone_number}
                    onChange={this.handleCheckboxChange('export_include_phone_number')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label={langText.Body.ExportBasicInfoMemo}
                    checked={export_include_memo}
                    onChange={this.handleCheckboxChange('export_include_memo')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ExportCallHistory}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="checkbox"
                    label={langText.Body.ExportCallHistoryLastCall}
                    checked={export_include_last_call}
                    onChange={this.handleCheckboxChange('export_include_last_call')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label={langText.Body.ExportCallHistoryTotalCount}
                    checked={export_include_total_call_count}
                    onChange={this.handleCheckboxChange('export_include_total_call_count')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card variant="info" className="mt-3">
            <Card.Header>
              <Card.Title>{langText.Body.ExportSettings}</Card.Title>
            </Card.Header>
            <Card.Body className="card-body-centered">
              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ExportEncoding}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ExportEncodingUtf8}
                    checked={export_encoding === 'utf8'}
                    onChange={this.handleRadioChange('export_encoding', 'utf8')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ExportEncodingShiftJIS}
                    checked={export_encoding === 'sjis'}
                    onChange={this.handleRadioChange('export_encoding', 'sjis')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ExportRange}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ExportRangeAll}
                    checked={export_range === 'all'}
                    onChange={this.handleRadioChange('export_range', 'all')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ExportRangeFiltered}
                    checked={export_range === 'filtered'}
                    onChange={this.handleRadioChange('export_range', 'filtered')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="cancel-execute"
            variant="outline-secondary"
            onClick={() => this.props.propSetState({ show: false })}
            disabled={export_processing}
          >
            {langText.Body.Cancel}
          </Button>
          <Button
            className="ok-execute"
            variant="primary"
            onClick={this.handleExport}
            disabled={export_processing}
          >
            {export_processing ? langText.Body.ImportProcessing : langText.Body.ExportExecute}
          </Button>
        </Modal.Footer>
        </div>
      </Modal>
    );
  }
}
